import React, { useState, useRef, useCallback, useEffect } from 'react';
import Map, { MapRef, Layer, Source } from 'react-map-gl';
import { FeatureCollection } from 'geojson';
import { Edit3, Crosshair } from 'react-feather';
import './PolygonDrawingMap.css';

interface PolygonDrawingMapProps {
  onPolygonComplete: (coordinates: number[][]) => void;
  initialCenter?: { lat: number; lng: number };
}

interface Point {
  lng: number;
  lat: number;
}

export const PolygonDrawingMap: React.FC<PolygonDrawingMapProps> = ({
  onPolygonComplete,
  initialCenter
}) => {
  const mapRef = useRef<MapRef>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [points, setPoints] = useState<Point[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(true);

  // Get user's location on mount
  useEffect(() => {
    if (initialCenter) {
      setUserLocation(initialCenter);
      setIsLoadingLocation(false);
      return;
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setIsLoadingLocation(false);
        },
        (error) => {
          console.log('Geolocation error:', error);
          // Fall back to Sydney CBD if location fails
          setUserLocation({ lat: -33.8688, lng: 151.2093 });
          setIsLoadingLocation(false);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    } else {
      // Fallback if geolocation not available
      setUserLocation({ lat: -33.8688, lng: 151.2093 });
      setIsLoadingLocation(false);
    }
  }, [initialCenter]);

  // Center map on user location
  const centerOnUserLocation = useCallback(() => {
    if (navigator.geolocation && mapRef.current) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(newLocation);
          mapRef.current?.flyTo({
            center: [newLocation.lng, newLocation.lat],
            zoom: 11,
            duration: 1000
          });
        },
        (error) => {
          console.log('Could not get location:', error);
        }
      );
    }
  }, []);

  // Convert points to GeoJSON for rendering
  const getPolygonGeoJSON = useCallback((): FeatureCollection => {
    if (points.length < 3) {
      return {
        type: 'FeatureCollection',
        features: []
      };
    }

    const coordinates = points.map(p => [p.lng, p.lat]);
    
    // Close the polygon if it's complete
    if (isComplete && points.length >= 3) {
      coordinates.push([points[0].lng, points[0].lat]);
    }

    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [coordinates]
          }
        }
      ]
    };
  }, [points, isComplete]);

  // Get line GeoJSON for drawing in progress
  const getLineGeoJSON = useCallback((): FeatureCollection => {
    if (points.length < 2) {
      return {
        type: 'FeatureCollection',
        features: []
      };
    }

    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: points.map(p => [p.lng, p.lat])
          }
        }
      ]
    };
  }, [points]);

  // Get points GeoJSON for markers
  const getPointsGeoJSON = useCallback((): FeatureCollection => {
    return {
      type: 'FeatureCollection',
      features: points.map((point, index) => ({
        type: 'Feature',
        properties: { index, isFirst: index === 0 },
        geometry: {
          type: 'Point',
          coordinates: [point.lng, point.lat]
        }
      }))
    };
  }, [points]);

  // Check if a point is close to the first point (for closing polygon)
  const isNearFirstPoint = useCallback((newPoint: Point): boolean => {
    if (points.length < 3) return false;
    
    const firstPoint = points[0];
    const distance = Math.sqrt(
      Math.pow(newPoint.lng - firstPoint.lng, 2) + 
      Math.pow(newPoint.lat - firstPoint.lat, 2)
    );
    
    // Close if within ~300m (roughly 0.003 degrees) - 3x larger tapable area
    return distance < 0.003;
  }, [points]);

  // Handle map click
  const handleMapClick = useCallback((event: any) => {
    if (!isDrawing) return;

    const { lng, lat } = event.lngLat;
    const newPoint: Point = { lng, lat };

    // Check if we should close the polygon
    if (isNearFirstPoint(newPoint) && points.length >= 3) {
      setIsComplete(true);
      setIsDrawing(false);
      
      // Return coordinates in the format expected by the zone config
      const coordinates = points.map(p => [p.lng, p.lat]);
      coordinates.push([points[0].lng, points[0].lat]); // Close the polygon
      
      onPolygonComplete(coordinates);
      return;
    }

    // Add new point
    setPoints(prev => [...prev, newPoint]);
  }, [isDrawing, points, onPolygonComplete, isNearFirstPoint]);

  // Start drawing
  const startDrawing = () => {
    setIsDrawing(true);
    setPoints([]);
    setIsComplete(false);
  };

  // Clear drawing
  const clearDrawing = () => {
    setIsDrawing(false);
    setPoints([]);
    setIsComplete(false);
  };

  // Complete polygon manually (minimum 3 points)
  const completePolygon = () => {
    if (points.length >= 3) {
      setIsComplete(true);
      setIsDrawing(false);
      
      const coordinates = points.map(p => [p.lng, p.lat]);
      coordinates.push([points[0].lng, points[0].lat]); // Close the polygon
      
      onPolygonComplete(coordinates);
    }
  };

  // Use the same Mapbox token as the main app
  const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';

  // Show loading state while getting location
  if (isLoadingLocation || !userLocation) {
    return (
      <div className="polygon-drawing-map">
        <div className="map-container map-loading">
          <div className="map-loading-content">
            <div className="map-loading-spinner"></div>
            <span>Getting your location...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="polygon-drawing-map">
      <div className="map-container">
        <Map
          ref={mapRef}
          initialViewState={{
            longitude: userLocation.lng,
            latitude: userLocation.lat,
            zoom: 11
          }}
          style={{ width: '100%', height: '100%' }}
          mapStyle="mapbox://styles/mapbox/streets-v11"
          mapboxAccessToken={MAPBOX_TOKEN}
          onClick={handleMapClick}
          cursor={isDrawing ? 'crosshair' : 'grab'}
        >
          {/* Polygon fill */}
          {isComplete && (
            <Source id="polygon" type="geojson" data={getPolygonGeoJSON()}>
              <Layer
                id="polygon-fill"
                type="fill"
                paint={{
                  'fill-color': '#3b82f6',
                  'fill-opacity': 0.2
                }}
              />
              <Layer
                id="polygon-stroke"
                type="line"
                paint={{
                  'line-color': '#3b82f6',
                  'line-width': 2
                }}
              />
            </Source>
          )}

          {/* Drawing line */}
          {isDrawing && points.length > 1 && (
            <Source id="drawing-line" type="geojson" data={getLineGeoJSON()}>
              <Layer
                id="drawing-line-layer"
                type="line"
                paint={{
                  'line-color': '#ef4444',
                  'line-width': 2,
                  'line-dasharray': [2, 2]
                }}
              />
            </Source>
          )}

          {/* Points */}
          {points.length > 0 && (
            <Source id="points" type="geojson" data={getPointsGeoJSON()}>
              <Layer
                id="points-layer"
                type="circle"
                paint={{
                  'circle-color': [
                    'case',
                    ['get', 'isFirst'],
                    '#10b981', // Green for first point
                    '#ef4444'  // Red for other points
                  ],
                  'circle-radius': [
                    'case',
                    ['get', 'isFirst'],
                    12, // 2x larger for first point
                    6   // Normal size for other points
                  ],
                  'circle-stroke-color': '#ffffff',
                  'circle-stroke-width': 2
                }}
              />
            </Source>
          )}
        </Map>
        
        {/* Recenter button */}
        <button 
          className="recenter-btn"
          onClick={centerOnUserLocation}
          title="Center on my location"
        >
          <Crosshair size={18} />
        </button>
        
        {/* Overlay instructions when drawing */}
        {isDrawing && (
          <div className="drawing-info-overlay">
            {points.length < 2 ? (
              <span>Tap map to add points</span>
            ) : points.length >= 3 ? (
              <span>Tap near first point to close</span>
            ) : (
              <span>Add one more point</span>
            )}
          </div>
        )}

        {/* Drawing controls - positioned inside map as overlay */}
        <div className="drawing-controls-overlay">
          {!isDrawing && !isComplete && (
            <button className="control-btn primary" onClick={startDrawing}>
              <Edit3 size={18} />
              Start Drawing
            </button>
          )}

          {isDrawing && (
            <div className="drawing-actions">
              <div className="action-buttons">
                {points.length >= 3 && (
                  <button className="control-btn success" onClick={completePolygon}>
                    Close shape
                  </button>
                )}
                <button className="control-btn secondary" onClick={clearDrawing}>
                  Clear
                </button>
              </div>
            </div>
          )}

          {isComplete && (
            <div className="completion-message">
              <span>✓ Area defined with {points.length} points</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 